/**
 * EmailTemplatesCatalog — iter87.199.81
 *
 * Read-only catalog of every transactional email the platform sends.
 * Rows come from `memory/email_registry.md`, augmented with live
 * `last_sent_at` + `sent_last_24h` counters when available. Clicking a
 * row opens a preview drawer that renders the email using synthetic
 * fixtures (no real send).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Mail, ExternalLink, X, Power } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const FAMILY_TONE = {
    auth: "text-volt border-volt/40 bg-volt/5",
    league: "text-cyber border-cyber/40 bg-cyber/5",
    draft: "text-blaze border-blaze/40 bg-blaze/5",
    broadcast: "text-neon border-neon/40 bg-neon/5",
    fallback: "text-zinc-400 border-zinc-700 bg-zinc-900/40",
};

export default function EmailTemplatesCatalog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [family, setFamily] = useState("all");
    const [preview, setPreview] = useState(null); // { template_id, subject, html, text }
    const [previewLoading, setPreviewLoading] = useState(false);
    // iter87.428 (user-requested) — To-do reminder email kill switch.
    const [killSwitch, setKillSwitch] = useState(null); // { emails_live, updated_at, updated_by }
    const [killSwitchSaving, setKillSwitchSaving] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.siteCommsMonitorEmailTemplates()
            .then((r) => setRows(r.templates || []))
            .catch((e) => toast.error(e?.response?.data?.detail || "Could not load email templates"))
            .finally(() => setLoading(false));
        api.siteCommsMonitorTodoReminderKillSwitch()
            .then(setKillSwitch)
            .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the to-do reminder kill switch"));
    }, []);

    const handleKillSwitchToggle = async (checked) => {
        setKillSwitchSaving(true);
        try {
            const r = await api.siteCommsMonitorSetTodoReminderKillSwitch(checked);
            setKillSwitch(r);
            toast.success(checked ? "To-do reminder emails are now LIVE." : "To-do reminder emails are suppressed again.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update the kill switch");
        } finally {
            setKillSwitchSaving(false);
        }
    };

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (family !== "all" && r.family !== family) return false;
            if (!needle) return true;
            return (
                r.template_id.toLowerCase().includes(needle)
                || (r.trigger || "").toLowerCase().includes(needle)
                || (r.copy_source || "").toLowerCase().includes(needle)
            );
        });
    }, [rows, q, family]);

    const familyCounts = useMemo(() => {
        const c = { all: rows.length };
        rows.forEach((r) => { c[r.family] = (c[r.family] || 0) + 1; });
        return c;
    }, [rows]);

    const openPreview = async (row) => {
        setPreviewLoading(true);
        setPreview({ template_id: row.template_id, subject: "…", html: null, text: null });
        try {
            const p = await api.siteCommsMonitorEmailPreview(row.template_id);
            setPreview(p);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="email-templates-catalog">
            {/* iter87.428 (user-requested) — To-do reminder email kill
                switch. DB-backed (system_settings.todo_reminder_emails_live),
                flips the cron's actual Resend send live, no redeploy. */}
            {killSwitch && (
                <div
                    className={`brutal-card p-4 flex items-start justify-between gap-4 ${killSwitch.emails_live ? "border-heat/60 bg-heat/5" : "border-emerald-500/40 bg-emerald-500/5"}`}
                    data-testid="todo-reminder-kill-switch-card"
                >
                    <div className="flex items-start gap-3">
                        <Power className={`w-4 h-4 mt-0.5 ${killSwitch.emails_live ? "text-heat" : "text-emerald-400"}`} />
                        <div>
                            <p className={`text-sm font-bold uppercase tracking-wider ${killSwitch.emails_live ? "text-heat" : "text-emerald-400"}`}>
                                To-do reminder emails — {killSwitch.emails_live ? "LIVE" : "Suppressed"}
                            </p>
                            <p className="text-[11px] text-zinc-500 mt-0.5 max-w-md">
                                {killSwitch.emails_live
                                    ? "The daily to-do reminder cron actually calls Resend for opted-in Pro+ users."
                                    : "The daily to-do reminder cron computes the digest but never calls Resend — safe for beta/preview."}
                            </p>
                            {killSwitch.updated_at && (
                                <p className="text-[10px] text-zinc-600 mt-1">
                                    Last changed {new Date(killSwitch.updated_at).toLocaleString()} by {killSwitch.updated_by || "site-admin"}
                                </p>
                            )}
                        </div>
                    </div>
                    <Switch
                        checked={!!killSwitch.emails_live}
                        onCheckedChange={handleKillSwitchToggle}
                        disabled={killSwitchSaving}
                        data-testid="todo-reminder-kill-switch-toggle"
                        aria-label="To-do reminder emails live"
                    />
                </div>
            )}
            <div className="flex items-baseline gap-2">
                <Mail className="w-4 h-4 text-volt" />
                <h2 className="font-display font-black text-lg">Email Templates</h2>
                <span className="text-xs text-zinc-500">
                    {loading ? "loading…" : `${rows.length} template${rows.length === 1 ? "" : "s"}`}
                </span>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
                Every transactional email the platform sends, sourced from{" "}
                <code className="text-zinc-300">memory/email_registry.md</code>.
                Copy changes happen by editing the source (`email_service.py` or
                `invite_copy_templates.py`) plus the registry row in the same commit.
                Click any row for a synthetic-fixture preview.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
                <input
                    type="text"
                    placeholder="Filter by template, trigger, or copy source…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="brutal-input flex-1 min-w-[280px] text-sm"
                    data-testid="email-catalog-search"
                />
                <div className="flex flex-wrap gap-1.5">
                    {["all", "auth", "league", "draft", "broadcast", "fallback"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setFamily(k)}
                            data-testid={`email-catalog-family-${k}`}
                            className={`pill text-[10px] font-black uppercase tracking-widest px-2 py-1 ${
                                family === k
                                    ? "bg-volt text-black border-volt"
                                    : "bg-transparent text-zinc-400 border-[#333] hover:text-white"
                            }`}
                        >
                            {k} · {familyCounts[k] || 0}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-3" data-testid="email-catalog-list">
                {filtered.map((r) => (
                    <div
                        key={r.template_id}
                        className="brutal-card p-4 space-y-3"
                        data-testid={`email-catalog-row-${r.template_id}`}
                    >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className={`pill text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${FAMILY_TONE[r.family] || FAMILY_TONE.fallback}`}>
                                        {r.family}
                                    </span>
                                    <p className="font-mono text-[11px] text-white truncate">{r.template_id}</p>
                                </div>
                                {r.subject_snippet && (
                                    <p className="text-xs text-zinc-300 leading-snug"><span className="text-zinc-500 uppercase tracking-widest text-[9px] font-black mr-2">Subject</span>{r.subject_snippet}</p>
                                )}
                            </div>
                            <button
                                onClick={() => openPreview(r)}
                                data-testid={`email-catalog-preview-${r.template_id}`}
                                className="pill text-[9px] font-black uppercase tracking-widest px-2 py-1 text-cyber border-cyber/40 hover:bg-cyber/10 shrink-0"
                            >
                                Preview <ExternalLink className="inline w-2.5 h-2.5" />
                            </button>
                        </div>
                        {r.body_snippet && (
                            <p className="text-[11px] text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-3 italic">
                                {r.body_snippet}
                            </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-[10px]">
                            <div>
                                <p className="text-zinc-500 uppercase tracking-widest font-black mb-0.5">Triggered by</p>
                                <p className="text-zinc-300 font-mono break-all">{r.trigger}</p>
                            </div>
                            <div>
                                <p className="text-zinc-500 uppercase tracking-widest font-black mb-0.5">Copy source</p>
                                <p className="text-zinc-300 font-mono break-all">{r.copy_source}</p>
                            </div>
                            <div>
                                <p className="text-zinc-500 uppercase tracking-widest font-black mb-0.5">Sends</p>
                                {r.last_sent_at ? (
                                    <p className="font-mono">
                                        <span className="text-white">{new Date(r.last_sent_at).toLocaleString()}</span>
                                        <br />
                                        <span className="text-volt">{r.sent_last_24h || 0} in 24h · {r.sent_total || 0} total</span>
                                    </p>
                                ) : (
                                    <p className="text-zinc-600">no telemetry yet</p>
                                )}
                            </div>
                        </div>
                        {r.notes && r.notes !== "" && (
                            <p className="text-[10px] text-zinc-500 leading-snug border-t border-zinc-900 pt-2">
                                <span className="uppercase tracking-widest font-black text-zinc-500 mr-1">Notes</span>
                                {r.notes}
                            </p>
                        )}
                    </div>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="brutal-card p-6 text-center text-xs text-zinc-500">
                        {rows.length === 0 ? "Registry not loaded" : "No matches"}
                    </div>
                )}
            </div>
            {preview && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setPreview(null)}
                    data-testid="email-preview-modal"
                >
                    <div
                        className="bg-[#0a0a0a] border-2 border-volt shadow-brutalBlue max-w-3xl w-full max-h-[85vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b-2 border-zinc-800">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-volt font-black">Preview · synthetic fixture</p>
                                <p className="font-mono text-sm text-white truncate">{preview.template_id}</p>
                            </div>
                            <button
                                onClick={() => setPreview(null)}
                                className="p-1 hover:bg-zinc-900"
                                data-testid="email-preview-close"
                            ><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-auto p-4 space-y-4">
                            {previewLoading ? (
                                <p className="text-xs text-zinc-500">Rendering…</p>
                            ) : (
                                <>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mb-1">Subject</p>
                                        <p className="text-sm text-white">{preview.subject}</p>
                                    </div>
                                    {preview.html && (
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mb-1">HTML</p>
                                            <div
                                                className="border border-zinc-800 rounded bg-white text-black overflow-auto"
                                                data-testid="email-preview-html"
                                                dangerouslySetInnerHTML={{ __html: preview.html }}
                                            />
                                        </div>
                                    )}
                                    {preview.text && (
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black mb-1">Text</p>
                                            <pre className="border border-zinc-800 bg-zinc-950 text-zinc-300 text-[11px] p-3 whitespace-pre-wrap font-mono" data-testid="email-preview-text">
                                                {preview.text}
                                            </pre>
                                        </div>
                                    )}
                                    {!preview.html && !preview.text && (
                                        <p className="text-xs text-zinc-500">This template doesn't expose a build helper — preview isn't available. Copy source: <code>{rows.find(r => r.template_id === preview.template_id)?.copy_source}</code></p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
